import React from 'react';
import { Box, Typography } from '@mui/material';
import STITCH_TYPES from '../stitchTypes';
import { useChartData } from './ChartDataProvider';

// Create stitch categories from the stitch types
const createStitchCategories = () => {
  const categories = {};
  Object.entries(STITCH_TYPES).forEach(([key, stitch]) => {
    const category = stitch.category || 'Other';
    if (!categories[category]) {
      categories[category] = [];
    }
    categories[category].push(key);
  });
  return categories;
};

const STITCH_CATEGORIES = createStitchCategories();

// StitchSymbol component for rendering stitch symbols
const StitchSymbol = ({ type, cellSize = 32, isSelected = false }) => {
  const stitch = STITCH_TYPES[type];
  if (!stitch || !stitch.symbol) {
    return <Box sx={{ width: cellSize, height: cellSize, border: '1px solid #ccc' }} />;
  }

  const { symbol } = stitch;
  if (!symbol.path) {
    // Empty symbol for knit stitch
    return (
      <Box 
        sx={{ 
          width: cellSize, 
          height: cellSize, 
          border: '1px solid #ccc',
          backgroundColor: isSelected ? '#e3f2fd' : '#f9f9f9'
        }} 
      />
    );
  }

  return (
    <Box sx={{ width: cellSize, height: cellSize, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg
        width={cellSize * 0.8}
        height={cellSize * 0.8}
        viewBox={symbol.viewBox || '0 0 24 24'}
        style={{ display: 'block' }}
      >
        <path
          d={symbol.path}
          stroke={symbol.stroke || '#333'}
          strokeWidth={symbol.strokeWidth || 1}
          fill={symbol.fill || 'none'}
        />
      </svg>
    </Box>
  );
};

const StitchPickerPanel = () => {
  const { selectedStitch, actions } = useChartData();

  const handleStitchSelect = (stitchKey) => {
    actions.setSelectedStitch(stitchKey);
    // Automatically enable stitch mode when a stitch is selected
    actions.setStitchMode(true);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Selected Stitch Details */}
      {selectedStitch && (
        <Box sx={{ mb: 2, bgcolor: 'grey.50', borderRadius: 1, flexShrink: 0 }}>
          <Typography variant="body2" fontWeight="bold">
            Selected: {STITCH_TYPES[selectedStitch]?.label || selectedStitch}
          </Typography>
          {STITCH_TYPES[selectedStitch]?.description && (
            <Typography variant="body2" color="text.secondary">
              {STITCH_TYPES[selectedStitch].description}
            </Typography>
          )}
          {STITCH_TYPES[selectedStitch]?.width > 1 && (
            <Typography variant="body2" color="text.secondary">
              Width: {STITCH_TYPES[selectedStitch].width} cells
            </Typography>
          )}
        </Box>
      )}
      
      {/* All Stitches by Category */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
      }}>
        {Object.entries(STITCH_CATEGORIES).map(([category, stitchKeys]) => (
          <Box key={category} sx={{ mb: 2 }}>
            {/* Category Header */}
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 'bold', 
                mb: 1,
                px: 1,
                py: 0.5,
                bgcolor: 'grey.100',
                borderRadius: 1
              }}
            >
              {category}
            </Typography>
            
            {/* Stitches in this category */}
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1
            }}>
              {stitchKeys.map((stitchKey) => {
                const stitch = STITCH_TYPES[stitchKey];
                const isSelected = selectedStitch === stitchKey;
                
                return (
                  <Box
                    key={stitchKey}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      border: '2px solid',
                      borderColor: isSelected ? '#1976d2' : '#bbb',
                      borderRadius: 1,
                      backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : '#fff',
                      cursor: 'pointer',
                      p: 1,
                      minWidth: 60,
                      '&:hover': {
                        borderColor: '#1976d2',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      },
                    }}
                    onClick={() => handleStitchSelect(stitchKey)}
                    title={stitch.description || stitch.label}
                  >
                    <StitchSymbol type={stitchKey} cellSize={40} isSelected={isSelected} />
                    <Typography variant="caption" align="center" sx={{ 
                      fontWeight: isSelected ? 'bold' : 'normal',
                      mt: 0.5,
                      fontSize: '0.7rem'
                    }}>
                      {stitch.label || stitchKey}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default StitchPickerPanel;
